import { useCallback, useEffect, useState, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormSection } from '@/components/shared/FormSection';
import { EditPageLayout } from '@/components/shared/EditPageLayout';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';
import type {
  Item,
  Branch,
  Category,
  CustomFieldDefinition,
  Shelf,
} from '@packages/shared-types';
import {
  getItemById,
  type UpdateItemPayload,
  updateItem,
  deleteItem,
} from '@/services/itemService';
import {
  listCustomFieldDefinitions,
  upsertItemCustomFieldValues,
} from '@/services/customFieldService';
import { listBranches } from '@/services/branchService';
import { listCategories } from '@/services/categoryService';
import { listShelves } from '@/services/shelfService';
import { useTenantSwitcher } from '@/contexts/TenantSwitcherContext';

const itemFormSchema = z.object({
  name: z.string().min(1, 'pages.items.form.nameRequired'),
  sku: z.string().min(1, 'pages.items.form.skuRequired'),
  rfidTagId: z.string().min(1, 'pages.items.form.rfidTagRequired'),
  description: z.string().optional(),
  categoryId: z.string().uuid().optional().or(z.literal('')),
  branchId: z.string().min(1, 'pages.items.form.branchRequired'),
  currentShelfId: z.string().uuid().optional().or(z.literal('')),
  currentPositionCell: z.string().optional().or(z.literal('')),
});

type ItemFormValues = z.infer<typeof itemFormSchema>;

export function EditItemPage(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { activeTenant } = useTenantSwitcher();
  const editTitle = useIndustryTranslation('pages.items.edit.title');
  const editDescription = useIndustryTranslation('pages.items.edit.description');
  const deleteDialogTitle = useIndustryTranslation('pages.items.dialogs.delete.title');
  const successUpdate = useIndustryTranslation('pages.items.success.update');
  const successDelete = useIndustryTranslation('pages.items.success.delete');
  const errorLoad = useIndustryTranslation('pages.items.error.load');
  const errorUpdate = useIndustryTranslation('pages.items.error.update');
  const errorDelete = useIndustryTranslation('pages.items.error.delete');
  const errorNotFound = useIndustryTranslation('pages.items.error.notFound');
  const categoryLabel = useIndustryTranslation('pages.items.form.category');
  const branchLabel = useIndustryTranslation('pages.items.form.branch');
  const shelfLabel = useIndustryTranslation('pages.items.form.shelf');
  const selectBranchLabel = useIndustryTranslation('pages.items.form.selectBranch');
  const selectCategoryLabel = useIndustryTranslation('pages.items.form.selectCategory');
  const selectShelfLabel = useIndustryTranslation('pages.items.form.selectShelf');
  const positionRowLabel = useIndustryTranslation('pages.shelves.fields.row');
  const positionCellLabel = useIndustryTranslation('pages.shelves.fields.cell');

  const [item, setItem] = useState<Item | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<
    CustomFieldDefinition[]
  >([]);
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, string | number>
  >({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBranches] = useState(false);
  const [isLoadingCategories] = useState(false);
  const [isLoadingShelves] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ItemFormValues>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: {
      name: '',
      sku: '',
      rfidTagId: '',
      description: '',
      categoryId: '',
      branchId: '',
      currentShelfId: '',
      currentPositionCell: '',
    },
  });

  const watchBranchId = watch('branchId');
  const watchCategoryId = watch('categoryId');
  const watchCurrentShelfId = watch('currentShelfId');

  // Filter shelves by selected branch
  const availableShelves = useMemo(() => {
    if (!watchBranchId) return [];
    return shelves.filter((shelf) => shelf.branchId === watchBranchId);
  }, [shelves, watchBranchId]);

  const selectedShelf = useMemo(
    () =>
      watchCurrentShelfId
        ? shelves.find((s) => s.id === watchCurrentShelfId) ?? null
        : null,
    [shelves, watchCurrentShelfId],
  );

  const positionCellsOptions = useMemo(() => {
    if (!selectedShelf) return [];
    const cells = selectedShelf.positionCells;
    if (cells?.length) return cells;
    const raw = (
      selectedShelf as Shelf & { position_cells?: string | null }
    ).position_cells?.trim();
    return raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];
  }, [selectedShelf]);

  const showPositionSelect =
    !!watchCurrentShelfId && positionCellsOptions.length > 0;

  const formatErrorMessage = useCallback(
    (message?: string): string | null => {
      if (!message) {
        return null;
      }

      return t(message);
    },
    [t],
  );

  const loadItem = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    try {
      const [itemData, branchesData, categoriesData, shelvesData] = await Promise.all([
        getItemById(id),
        listBranches(),
        listCategories(),
        listShelves(),
      ]);

      setItem(itemData);
      setBranches(branchesData);
      setCategories(categoriesData);
      setShelves(shelvesData);

      reset({
        name: itemData.name,
        sku: itemData.sku,
        rfidTagId: itemData.rfidTagId ?? '',
        description: itemData.description || '',
        categoryId: itemData.categoryId || '',
        branchId: itemData.branchId,
        currentShelfId: itemData.currentShelfId || '',
        currentPositionCell: itemData.currentPositionCell ?? '',
      });

      setCustomFieldValues(itemData.customFieldValues ?? {});
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : errorLoad,
      );
    } finally {
      setIsLoading(false);
    }
  }, [id, reset, errorLoad, activeTenant?.id]);

  useEffect(() => {
    void loadItem();
  }, [loadItem]);

  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    const loadDefs = async (): Promise<void> => {
      const [categoryDefs, tenantDefs] = await Promise.all([
        watchCategoryId
          ? listCustomFieldDefinitions(watchCategoryId)
          : Promise.resolve([]),
        listCustomFieldDefinitions(null),
      ]);
      if (cancelled) return;
      const keySet = new Set<string>();
      const merged: CustomFieldDefinition[] = [];
      for (const d of [...categoryDefs, ...tenantDefs]) {
        if (!keySet.has(d.key)) {
          keySet.add(d.key);
          merged.push(d);
        }
      }
      setCustomFieldDefinitions(merged);
      setCustomFieldValues((prev) => {
        const defKeys = new Set(merged.map((d) => d.key));
        const filtered: Record<string, string | number> = {};
        for (const [key, val] of Object.entries(prev)) {
          if (defKeys.has(key)) filtered[key] = val;
        }
        return filtered;
      });
    };
    void loadDefs();
    return () => {
      cancelled = true;
    };
  }, [watchCategoryId, item?.id]);

  const handleSubmitItem = async (
    values: ItemFormValues,
  ): Promise<void> => {
    if (!id) return;
    setIsSaving(true);
    setError(null);

    const payload: UpdateItemPayload = {
      name: values.name,
      sku: values.sku,
      rfidTagId: values.rfidTagId,
      description: values.description || undefined,
      categoryId: values.categoryId || undefined,
      branchId: values.branchId,
      currentShelfId: values.currentShelfId || undefined,
      currentPositionCell: values.currentPositionCell || undefined,
    };

    try {
      await updateItem(id, payload);
      const valuesToSave: Record<string, string | number> = {};
      for (const def of customFieldDefinitions) {
        const val = customFieldValues[def.key];
        valuesToSave[def.key] = val ?? '';
      }
      if (customFieldDefinitions.length > 0) {
        await upsertItemCustomFieldValues(id, valuesToSave);
      }
      await loadItem();
      toast.success(successUpdate);
      // Navigate back to details page
      navigate(`/items/${id}`);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorUpdate;
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!id || !item) return;
    setIsDeleting(true);
    try {
      await deleteItem(id);
      toast.success(successDelete);
      navigate('/items');
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : errorDelete;
      setError(errorMessage);
      toast.error(errorMessage);
      setIsDeleting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void handleSubmit(handleSubmitItem)();
  };

  if (!id) {
    return (
      <div className="p-6 text-sm text-destructive">
        {errorNotFound}
      </div>
    );
  }

  return (
    <EditPageLayout
      title={editTitle}
      description={item ? editDescription : undefined}
      isLoading={isLoading}
      error={error}
      formId="item-edit-form"
      onSubmit={handleFormSubmit}
      onCancel={() => navigate(-1)}
      cancelLabel={t('pages.items.actions.cancel')}
      onSave={() => void handleSubmit(handleSubmitItem)()}
      saveLabel={t('pages.items.actions.update')}
      savingLabel={t('pages.items.actions.updating')}
      isSaving={isSaving}
      onDelete={handleDelete}
      deleteLabel={t('pages.items.actions.delete')}
      deletingLabel={t('pages.items.actions.deleting')}
      isDeleting={isDeleting}
      deleteDialogTitle={deleteDialogTitle}
      deleteDialogDescription={t('pages.items.dialogs.delete.description', {
        name: item?.name,
      })}
      entityExists={!!item}
    >
      <form
        id="item-edit-form"
        className="space-y-6"
        onSubmit={handleSubmit(handleSubmitItem)}
      >
        <FormSection
          title={t('pages.items.form.name')}
          description={t('pages.items.form.name')}
          error={
            errors.name
              ? formatErrorMessage(errors.name.message) ??
                (errors.name.message as string)
              : null
          }
        >
          <Input
            id="item-name"
            {...register('name')}
            placeholder={t('pages.items.form.name')}
            disabled={isSaving}
          />
        </FormSection>

        <div className="grid gap-6 md:grid-cols-2">
          <FormSection
            title={t('pages.items.form.sku')}
            description={t('pages.items.form.sku')}
            error={
              errors.sku
                ? formatErrorMessage(errors.sku.message) ??
                  (errors.sku.message as string)
                : null
            }
          >
            <Input
              id="item-sku"
              {...register('sku')}
              placeholder={t('pages.items.form.sku')}
              disabled={isSaving}
            />
          </FormSection>

          <FormSection
            title={t('pages.items.form.rfidTag')}
            description={t('pages.items.form.rfidTag')}
            error={
              errors.rfidTagId
                ? formatErrorMessage(errors.rfidTagId.message) ??
                  (errors.rfidTagId.message as string)
                : null
            }
          >
            <Input
              id="item-rfidTag"
              {...register('rfidTagId')}
              placeholder={t('pages.items.form.rfidTag')}
              disabled={isSaving}
            />
          </FormSection>
        </div>

        <FormSection
          title={t('pages.items.form.description')}
          description={t('pages.items.form.description')}
        >
          <Input
            id="item-description"
            {...register('description')}
            placeholder={t('pages.items.form.description')}
            disabled={isSaving}
          />
        </FormSection>

        <div className="grid gap-6 md:grid-cols-2">
          <FormSection
            title={categoryLabel}
            description={selectCategoryLabel}
          >
            <Controller
              name="categoryId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || undefined}
                  onValueChange={(value) => field.onChange(value || undefined)}
                  disabled={isSaving || isLoadingCategories}
                >
                  <SelectTrigger id="item-category">
                    <SelectValue placeholder={selectCategoryLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormSection>

          <FormSection
            title={branchLabel}
            description={selectBranchLabel}
            error={
              errors.branchId
                ? formatErrorMessage(errors.branchId.message) ??
                  (errors.branchId.message as string)
                : null
            }
          >
            <Controller
              name="branchId"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    setValue('currentShelfId', '');
                    setValue('currentPositionCell', '');
                  }}
                  disabled={isSaving || isLoadingBranches}
                >
                  <SelectTrigger id="item-branch">
                    <SelectValue placeholder={selectBranchLabel} />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormSection>
        </div>

        <FormSection
          title={shelfLabel}
          description={selectShelfLabel}
        >
          <Controller
            name="currentShelfId"
            control={control}
            render={({ field }) => (
                  <Select
                    value={field.value || undefined}
                    onValueChange={(value) => {
                      field.onChange(value || undefined);
                      setValue('currentPositionCell', '');
                    }}
                    disabled={isSaving || isLoadingShelves || !watchBranchId}
                  >
                    <SelectTrigger id="item-shelf">
                      <SelectValue placeholder={selectShelfLabel} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableShelves.length === 0 ? (
                        <SelectItem value="none" disabled>
                          {watchBranchId
                            ? t('pages.items.form.noShelves')
                            : t('pages.items.form.selectBranchFirst')}
                        </SelectItem>
                      ) : (
                    availableShelves.map((shelf) => (
                      <SelectItem key={shelf.id} value={shelf.id}>
                        {shelf.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            )}
          />
        </FormSection>

        {showPositionSelect && (
          <FormSection
            title={t('pages.items.form.position')}
            description={t('pages.items.form.selectPosition')}
          >
            <Controller
              name="currentPositionCell"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value || 'none'}
                  onValueChange={(value) =>
                    field.onChange(value === 'none' ? '' : value)
                  }
                  disabled={isSaving}
                >
                  <SelectTrigger id="item-position">
                    <SelectValue placeholder={t('pages.items.form.selectPosition')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t('pages.items.form.noSpecificPosition')}
                    </SelectItem>
                    {positionCellsOptions.map((positionKey) => {
                      const dashIdx = positionKey.indexOf('-');
                      const row = dashIdx <= 0 ? positionKey : positionKey.slice(0, dashIdx);
                      const cell = dashIdx < 0 ? '' : positionKey.slice(dashIdx + 1);
                      const label =
                        row && cell
                          ? `${positionRowLabel} ${row}, ${positionCellLabel} ${cell}`
                          : positionKey;
                      return (
                        <SelectItem key={positionKey} value={positionKey}>
                          {label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            />
          </FormSection>
        )}

        {customFieldDefinitions.length > 0 && (
          <FormSection
            title={t('pages.customFields.title')}
            description={t('pages.items.details.sections.customFields')}
          >
            <div className="space-y-4">
              {customFieldDefinitions.map((def) => (
                <div key={def.id} className="space-y-2">
                  <label
                    htmlFor={`cf-${def.key}`}
                    className="text-sm font-medium"
                  >
                    {def.name}
                    {def.required && ' *'}
                  </label>
                  {def.type === 'textarea' ? (
                    <textarea
                      id={`cf-${def.key}`}
                      className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={String(customFieldValues[def.key] ?? '')}
                      onChange={(e) =>
                        setCustomFieldValues((prev) => ({
                          ...prev,
                          [def.key]: e.target.value,
                        }))
                      }
                      disabled={isSaving}
                    />
                  ) : def.type === 'options' && def.options?.length ? (
                    <Select
                      value={String(customFieldValues[def.key] ?? '')}
                      onValueChange={(v) =>
                        setCustomFieldValues((prev) => ({
                          ...prev,
                          [def.key]: v,
                        }))
                      }
                      disabled={isSaving}
                    >
                      <SelectTrigger id={`cf-${def.key}`}>
                        <SelectValue placeholder={t('pages.customFields.form.selectOption')} />
                      </SelectTrigger>
                      <SelectContent>
                        {def.options.map((opt) => (
                          <SelectItem key={opt} value={opt}>
                            {opt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={`cf-${def.key}`}
                      type={def.type === 'number' ? 'number' : def.type === 'url' ? 'url' : 'text'}
                      value={String(customFieldValues[def.key] ?? '')}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomFieldValues((prev) => ({
                          ...prev,
                          [def.key]: def.type === 'number' ? (val === '' ? '' : Number(val)) : val,
                        }));
                      }}
                      disabled={isSaving}
                    />
                  )}
                </div>
              ))}
            </div>
          </FormSection>
        )}
      </form>
    </EditPageLayout>
  );
}
